import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Warehouse, Plus, Loader2, Package, ArrowDownToLine, MapPin, ClipboardList, Clock, BarChart3, Receipt, Timer, QrCode } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

import { API, authHeaders } from "./warehouse/shared";
import InventoryTable from "./warehouse/InventoryTable";
import MovementsTable from "./warehouse/MovementsTable";
import LocationsTable from "./warehouse/LocationsTable";
import OrdersTable from "./warehouse/OrdersTable";
import RequestsTable from "./warehouse/RequestsTable";
import MovementForm from "./warehouse/MovementForm";
import LocationForm from "./warehouse/LocationForm";
import OrderForm from "./warehouse/OrderForm";
import MovementEditForm from "./warehouse/MovementEditForm";
import RequestEditForm from "./warehouse/RequestEditForm";
import CreateShipmentForm from "./warehouse/CreateShipmentForm";
import QuickExitModal from "./warehouse/QuickExitModal";
import QuickEntryModal from "./warehouse/QuickEntryModal";
import ReportTab from "./warehouse/ReportTab";
import StorageBillingTab from "./warehouse/StorageBillingTab";
import HoursTab from "./warehouse/HoursTab";

const TABS = [
  { id: "inventory", label: "Inventario", icon: Package },
  { id: "movements", label: "Movimenti", icon: ArrowDownToLine },
  { id: "orders", label: "Ordini", icon: ClipboardList },
  { id: "requests", label: "Richieste", icon: Clock },
  { id: "hours", label: "Ore Lavorate", icon: Timer },
  { id: "billing", label: "Fatturazione", icon: Receipt },
  { id: "report", label: "Report", icon: BarChart3 },
  { id: "locations", label: "Posizioni", icon: MapPin },
];

export default function AdminWarehouse() {
  const [tab, setTab] = useState("inventory");
  const [stats, setStats] = useState(null);
  const [inventory, setInventory] = useState([]);
  const [movements, setMovements] = useState([]);
  const [locations, setLocations] = useState([]);
  const [orders, setOrders] = useState([]);
  const [requests, setRequests] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(null);
  const [editingOrder, setEditingOrder] = useState(null);
  const [editingMovement, setEditingMovement] = useState(null);
  const [editingRequest, setEditingRequest] = useState(null);
  const [shipmentRequest, setShipmentRequest] = useState(null);
  const [quickExitItem, setQuickExitItem] = useState(null);
  const [quickEntryItem, setQuickEntryItem] = useState(null);
  const [filterClient, setFilterClient] = useState("");

  const load = useCallback(async () => {
    try {
      const [statsR, invR, movR, locR, cliR, ordR, reqR] = await Promise.all([
        axios.get(`${API}/admin/warehouse/stats`, authHeaders()),
        axios.get(`${API}/admin/warehouse/inventory`, authHeaders()),
        axios.get(`${API}/admin/warehouse/movements`, authHeaders()),
        axios.get(`${API}/admin/warehouse/locations`, authHeaders()),
        axios.get(`${API}/admin/clients`, authHeaders()),
        axios.get(`${API}/admin/warehouse/orders`, authHeaders()),
        axios.get(`${API}/admin/warehouse/requests`, authHeaders()),
      ]);
      setStats(statsR.data);
      setInventory(invR.data);
      setMovements(movR.data);
      setLocations(locR.data);
      setClients(cliR.data.filter(c => c.warehouse_enabled));
      setOrders(ordR.data);
      setRequests(reqR.data);
    } catch { toast.error("Errore caricamento dati magazzino"); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="animate-spin text-[#FF5200]" size={32} /></div>;

  const filteredInv = filterClient ? inventory.filter(i => i.client_email === filterClient) : inventory;
  const filteredMov = filterClient ? movements.filter(m => m.client_email === filterClient) : movements;
  const filteredOrd = filterClient ? orders.filter(o => o.client_email === filterClient) : orders;

  const deleteMovement = async (id) => {
    const mov = movements.find((m) => m.id === id);
    const typeLbl = mov?.movement_type === "ingresso" ? "INGRESSO" : "USCITA";
    const rollbackLbl = mov?.movement_type === "ingresso"
      ? "gli articoli caricati verranno scaricati dall'inventario"
      : "gli articoli usciti verranno ripristinati in inventario";
    const costLbl = Number(mov?.cost || 0) > 0
      ? ` Il costo di ${Number(mov.cost).toFixed(2)} € verrà rimosso dalla fattura del mese.`
      : "";
    if (!window.confirm(
      `Eliminare questo movimento di ${typeLbl}?\n\n` +
      `Attenzione: ${rollbackLbl}.${costLbl}\n\nOperazione irreversibile.`
    )) return;
    try {
      const r = await axios.delete(`${API}/admin/warehouse/movements/${id}`, authHeaders());
      load();
      const parts = [];
      if (r.data?.inventory_deleted) parts.push(`${r.data.inventory_deleted} articoli rimossi`);
      if (r.data?.inventory_updated) parts.push(`${r.data.inventory_updated} articoli aggiornati`);
      if (r.data?.cost_removed > 0) parts.push(`costo ${Number(r.data.cost_removed).toFixed(2)} € rimosso da fattura`);
      toast.success(parts.length ? `Movimento eliminato — ${parts.join(", ")}` : "Movimento eliminato");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore eliminazione");
    }
  };
  const deleteLocation = async (id) => { await axios.delete(`${API}/admin/warehouse/locations/${id}`, authHeaders()); load(); toast.success("Posizione eliminata"); };
  const approveRequest = async (id, force = false) => {
    try {
      const url = `${API}/admin/warehouse/requests/${id}/approve${force ? "?force=true" : ""}`;
      await axios.put(url, {}, authHeaders());
      load();
      toast.success(force ? "Richiesta approvata (stock in negativo)" : "Richiesta approvata — ordine creato");
    } catch (err) {
      const detail = err.response?.data?.detail || "Errore approvazione";
      // If stock insufficient, ask admin if they want to force
      if (err.response?.status === 400 && detail.includes("Stock insufficiente")) {
        const confirmMsg = `${detail.replace(" Aggiungi 'force=true' per approvare comunque (lo stock andra in negativo).", "")}\n\nVuoi approvare comunque? Lo stock andra in negativo.`;
        if (window.confirm(confirmMsg)) {
          return approveRequest(id, true);
        }
        toast.warning("Approvazione annullata");
      } else {
        toast.error(detail);
      }
    }
  };
  const rejectRequest = async (id) => {
    try {
      await axios.put(`${API}/admin/warehouse/requests/${id}/reject`, {}, authHeaders());
      load();
      toast.success("Richiesta rifiutata");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore rifiuto");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-warehouse">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Articoli in Stock", value: stats?.total_items || 0, color: "bg-emerald-500" },
          { label: "Posizioni", value: stats?.total_locations || 0, color: "bg-blue-500" },
          { label: "Movimenti", value: stats?.total_movements || 0, color: "bg-amber-500" },
          { label: "Clienti Attivi", value: stats?.warehouse_clients?.length || 0, color: "bg-violet-500" },
        ].map((s, i) => (
          <div key={i} className="bg-white rounded-sm border border-zinc-200 p-4">
            <div className={`${s.color} w-10 h-10 rounded-sm flex items-center justify-center mb-3`}>
              <Warehouse size={20} className="text-white" />
            </div>
            <p className="text-2xl font-bold text-[#09090B]">{s.value}</p>
            <p className="text-xs text-zinc-500 font-bold uppercase tracking-wider">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Tabs + Filter */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex bg-white border border-zinc-200 rounded-sm overflow-hidden">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-bold uppercase tracking-wide transition-colors ${tab === t.id ? "bg-[#FF5200] text-white" : "text-zinc-500 hover:text-[#09090B]"}`}>
              <t.icon size={16} /> {t.label}
            </button>
          ))}
        </div>
        {clients.length > 0 && tab !== "locations" && (
          <select value={filterClient} onChange={e => setFilterClient(e.target.value)} data-testid="filter-client"
            className="border border-zinc-200 rounded-sm px-3 py-2 text-sm bg-white">
            <option value="">Tutti i clienti</option>
            {clients.map(c => <option key={c.email} value={c.email}>{c.company || c.name} ({c.client_code})</option>)}
          </select>
        )}
        <div className="flex-1" />
        {tab === "movements" && (
          <>
            <Link to="/admin/magazzino/scan" data-testid="scan-qr-btn"
              className="flex items-center gap-2 bg-purple-600 text-white px-4 py-2.5 rounded-sm text-sm font-bold uppercase hover:bg-purple-700 transition-colors">
              <QrCode size={16} /> Scan QR
            </Link>
            <button onClick={() => setShowForm("movement")} data-testid="new-movement-btn"
              className="flex items-center gap-2 bg-[#FF5200] text-white px-4 py-2.5 rounded-sm text-sm font-bold uppercase hover:bg-[#e64a00] transition-colors">
              <Plus size={16} /> Nuovo Movimento
            </button>
          </>
        )}
        {tab === "orders" && (
          <button onClick={() => { setEditingOrder(null); setShowForm("order"); }} data-testid="new-order-btn"
            className="flex items-center gap-2 bg-[#FF5200] text-white px-4 py-2.5 rounded-sm text-sm font-bold uppercase hover:bg-[#e64a00] transition-colors">
            <Plus size={16} /> Prepara Ordine
          </button>
        )}
        {tab === "locations" && (
          <button onClick={() => setShowForm("location")} data-testid="new-location-btn"
            className="flex items-center gap-2 bg-[#09090B] text-white px-4 py-2.5 rounded-sm text-sm font-bold uppercase hover:bg-zinc-800 transition-colors">
            <Plus size={16} /> Nuova Posizione
          </button>
        )}
      </div>

      {/* Content */}
      {tab === "inventory" && <InventoryTable items={filteredInv} onQuickExit={setQuickExitItem} onQuickEntry={setQuickEntryItem} />}
      {tab === "movements" && <MovementsTable items={filteredMov} onDelete={deleteMovement} onEdit={setEditingMovement} onRefresh={load} />}
      {tab === "orders" && <OrdersTable items={filteredOrd} onEdit={(o) => { setEditingOrder(o); setShowForm("order"); }} onRefresh={load} />}
      {tab === "requests" && <RequestsTable items={requests}
        onApprove={approveRequest}
        onReject={rejectRequest}
        onEdit={setEditingRequest}
        onCreateShipment={setShipmentRequest}
      />}
      {tab === "report" && <ReportTab clients={clients} />}
      {tab === "hours" && <HoursTab clients={clients} />}
      {tab === "billing" && <StorageBillingTab />}
      {tab === "locations" && <LocationsTable items={locations} onDelete={deleteLocation} />}

      {/* Modals */}
      {showForm === "movement" && <MovementForm clients={clients} locations={locations} onClose={() => setShowForm(null)} onSave={load} />}
      {showForm === "location" && <LocationForm onClose={() => setShowForm(null)} onSave={load} />}
      {showForm === "order" && <OrderForm clients={clients} inventory={inventory} editOrder={editingOrder} onClose={() => { setShowForm(null); setEditingOrder(null); }} onSave={load} />}
      {editingMovement && <MovementEditForm movement={editingMovement} locations={locations} onClose={() => setEditingMovement(null)} onSave={load} />}
      {editingRequest && <RequestEditForm request={editingRequest} onClose={() => setEditingRequest(null)} onSave={load} />}
      {shipmentRequest && <CreateShipmentForm request={shipmentRequest} onClose={() => setShipmentRequest(null)} onSave={load} />}
      {quickExitItem && <QuickExitModal item={quickExitItem} onClose={() => setQuickExitItem(null)} onSaved={load} />}
      {quickEntryItem && <QuickEntryModal item={quickEntryItem} onClose={() => setQuickEntryItem(null)} onSaved={load} />}
    </div>
  );
}
